import React, { useContext, useState, useEffect } from "react";
import AdminNavBar from "@/components/AdminNavBar";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, ArrowLeft, Medal } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLeaderboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [competition, setCompetition] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard");
    } else if (user) {
      fetchData();
    }
  }, [user, id]);

  const fetchData = async () => {
    try {
      const [compResp, leaderboardResp] = await Promise.all([
        axios.get(`${API}/admin/competitions/${id}`, { withCredentials: true }),
        axios.get(`${API}/admin/competitions/${id}/leaderboard`, { withCredentials: true })
      ]);

      setCompetition(compResp.data);
      setLeaderboard(leaderboardResp.data);

      // Surface par/SI/CR/SR if the comp is linked to a course
      if (compResp.data?.course_id) {
        try {
          const courseResp = await axios.get(`${API}/courses/${compResp.data.course_id}`, { withCredentials: true });
          setCourse(courseResp.data);
        } catch (_e) {
          setCourse(null);
        }
      }
    } catch (error) {
      console.error("Leaderboard fetch error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  // Compute active tee + par for the current hole format
  const teeInfo = (() => {
    if (!course || !competition) return null;
    const tees = course.tees || [];
    const tee = (competition.tee_id && tees.find(t => t.id === competition.tee_id)) || tees[0];
    if (!tee) return null;
    const fmt = competition.hole_format || "18";
    const [start, end] = fmt === "front_9" ? [1, 9] : fmt === "back_9" ? [10, 18] : [1, 18];
    const totalPar = (tee.holes || [])
      .filter(h => h.hole >= start && h.hole <= end)
      .reduce((s, h) => s + (parseInt(h.par) || 0), 0);
    return { tee, totalPar };
  })();
  const formatVsPar = (score, par) => {
    if (!score || !par) return null;
    const diff = score - par;
    if (diff === 0) return "E";
    return diff > 0 ? `+${diff}` : `${diff}`;
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
      <AdminNavBar />
        <div className="text-xl text-ink-muted">Loading...</div>
      </div>
    );
  }

  if (!competition && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
      <AdminNavBar />
        <div className="text-center">
          <div className="text-xl text-ink-muted mb-4">Competition not found</div>
          <Button onClick={() => navigate("/admin/competitions")}>
            Back to Competitions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <AdminNavBar />
      {/* Header */}
      <header className="bg-white border-b border-line py-6 px-8 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Logo variant="icon" size={36} />
            <h1 className="text-xl font-semibold text-ink tracking-tight">Golf Eclectic</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/competitions")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Competitions</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-8 py-12">
        {competition && (
          <>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-ink tracking-tight mb-2">{competition.name}</h2>
              <p className="text-lg text-ink-muted">Leaderboard - Net Scores</p>
            </div>

            {/* Course / par summary strip — only when comp has a linked course */}
            {teeInfo && (
              <div
                data-testid="admin-lb-course-strip"
                className="max-w-4xl mx-auto mb-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-3 rounded-lg bg-app border border-line text-sm"
              >
                <Trophy className="w-4 h-4 text-accent flex-shrink-0" />
                <span className="text-ink font-medium">
                  {course?.club_name && course.club_name !== course?.name ? `${course.club_name} · ` : ""}{course?.name}
                </span>
                {teeInfo.tee?.name && (
                  <>
                    <span className="text-ink-soft">·</span>
                    <span className="text-ink-muted">{teeInfo.tee.name}{teeInfo.tee.gender ? ` (${teeInfo.tee.gender})` : ""}</span>
                  </>
                )}
                <span className="text-ink-soft">·</span>
                <span className="font-mono tabular-nums text-ink-muted">
                  par <span className="text-ink font-semibold">{teeInfo.totalPar}</span>
                </span>
                {teeInfo.tee?.course_rating && (
                  <>
                    <span className="text-ink-soft">·</span>
                    <span className="font-mono tabular-nums text-ink-muted">CR {teeInfo.tee.course_rating}</span>
                  </>
                )}
                {teeInfo.tee?.slope_rating && (
                  <>
                    <span className="text-ink-soft">·</span>
                    <span className="font-mono tabular-nums text-ink-muted">SR {teeInfo.tee.slope_rating}</span>
                  </>
                )}
              </div>
            )}

            <Card className="max-w-4xl mx-auto">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-warm" />
                  Current Standings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="space-y-3">
                    {leaderboard.map((player, index) => (
                      <div
                        key={player.player_id}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                          index === 0
                            ? "bg-warm-tint/40 border-warm/40"
                            : index === 1
                            ? "bg-app border-line-strong"
                            : index === 2
                            ? "bg-warm-tint/40 border-warm/40"
                            : "bg-white border-line"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-12 h-12">
                            {index === 0 && <Medal className="w-8 h-8 text-warm" />}
                            {index === 1 && <Medal className="w-8 h-8 text-ink-soft" />}
                            {index === 2 && <Medal className="w-8 h-8 text-warm" />}
                            {index > 2 && (
                              <span className="text-xl font-semibold text-ink tracking-tight-soft">
                                {index + 1}
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-lg text-ink">
                              {player.user_name}
                            </div>
                            <div className="text-sm text-ink-muted">
                              {player.user_email}
                            </div>
                            <div className="text-xs text-ink-soft mt-1">
                              Handicap: {player.course_handicap !== undefined ? player.course_handicap : "N/A"}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-ink-muted mb-1">Net Score</div>
                          <div className="text-xl font-semibold text-ink tracking-tight">
                            {player.net_total}
                          </div>
                          <div className="text-xs text-ink-soft mt-1 font-mono tabular-nums">
                            Gross: {player.gross_total}
                            {(() => {
                              const vsPar = teeInfo ? formatVsPar(player.gross_total, teeInfo.totalPar) : null;
                              if (!vsPar) return null;
                              return (
                                <span
                                  data-testid={`admin-lb-vspar-${player.player_id}`}
                                  className={`ml-1.5 ${
                                    vsPar.startsWith("-") ? "text-accent font-semibold"
                                    : vsPar === "E" ? "text-ink"
                                    : "text-ink-muted"
                                  }`}
                                >({vsPar})</span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-ink-muted">
                    No players have submitted scores yet
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
